import React, { useState } from 'react';
import { Breadcrumbs, Typography, Link, AppBar, Box, Button, Grid } from '@material-ui/core';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import mime from 'mime-types';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import SendOutlinedIcon from '@material-ui/icons/SendOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { useAPI, VerticalBoxContainer, Spinner, TabPanel } from '@shared/components';
import PageHeader from '../../../components/PageHeader';
import useGlobalAlertContext from '../../../hooks/useGlobalAlertContext';
import { a11yProps } from '../../../utils/tabFormatter';
import useTheme from '../../../hooks/useTheme';
import axios from '../../../utils/axios';
import Attributes from './Attributes';
import Comments from './Comments';
import DocumentAttachments from './DocumentAttachments';
import DocumentModal from '../Modal';
import StatusModal from '../StatusModal';
import { AntTabs, AntTab, PageContentWrapper } from './style';

const Details = ({ previousPath = '/responsible-organization' }) => {
  const theme = useTheme();
  const { id } = useParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedDocument, setDocument] = useState({});
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();
  const history = useHistory();

  const [downloadLoading, setDownloadLoading] = useState(false);

  const getDocumentFile = async (documentId, extension) => {
    setDownloadLoading(true);
    try {
      const { data } = await axios.get(`get-document/${documentId}`);
      const { documentFile } = data;
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(documentFile.data).buffer], {
          type: mime.lookup(extension) || 'application/pdf',
        }),
      );

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('target', '_blank');

      link.click();
    } catch (documentError) {
      sendErrorAlertData(
        `${documentError?.response?.data?.details?.reason ||
        documentError?.response?.data?.error?.message ||
        `Došlo je do greške prilikom preuzimanja dokumenta`
        }`,
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleChange = (_, newValue) => {
    setCurrentTab(newValue);
  };

  const tabs = [
    {
      name: 'Atributi (metapodaci) za dati dokument',
      component: <Attributes parentDataId={id} setDocument={setDocument} />,
    },
    {
      name: 'Prilozi dokumenta',
      component: <DocumentAttachments parentDataId={id} />,
    },
    {
      name: 'Komentari',
      component: <Comments parentDataId={id} />,
    },
  ];

  const [, , loading] = useAPI(
    () =>
      axios
        .get(`documents/${id}`, {
          params: {
            screen: 'EKRAN1',
          },
        })
        .then(({ data }) => {
          setDocument(data);
        })
        .catch((error) => {
          sendErrorAlertData(
            `${error?.response?.data?.details?.reason ||
            error?.response?.data?.error?.message ||
            `Došlo je do greške prilikom preuzimanja informacija o dokumentu`
            }`,
          );
          history.replace(previousPath);
        }),
    [id],
  );

  return (
    <VerticalBoxContainer>
      <DocumentModal
        document={selectedDocument}
        setDocument={setDocument}
        resetDocument={() => { }}
        visible={documentModalVisible}
        setVisible={setDocumentModalVisible}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={() => { }}
        documentLoading={loading}
      />
      <StatusModal
        document={selectedDocument}
        setDocument={setDocument}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={() => history.goBack()}
        visible={statusModalVisible}
        setVisible={setStatusModalVisible}
        documentLoading={loading}
        detailScreen
      />
      <PageHeader
        showBackIcon
        title={`Pregled: Pridruživanje odgovorne osobe ${id}`}
        subtitle="Detalji dokumenta"
        extra={
          <>
            <Grid container spacing={4}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={loading ? <Spinner size={15} /> : <SendOutlinedIcon />}
                  disabled={loading}
                  onClick={() => setStatusModalVisible(true)}
                >
                  <Box fontSize="small">Promjena statusa</Box>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    downloadLoading ? <Spinner size={15} /> : <InsertDriveFileOutlinedIcon />
                  }
                  disabled={downloadLoading}
                  onClick={() => {
                    getDocumentFile(id, selectedDocument.documentExtension);
                  }}
                >
                  <Box fontSize="small">Prikaži dokument</Box>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <Spinner size={15} /> : <EditOutlinedIcon />}
                  disabled={loading}
                  onClick={() => setDocumentModalVisible(true)}
                >
                  <Box fontSize="small">Uredi dokument</Box>
                </Button>
              </Grid>
            </Grid>
          </>
        }
        history={history}
      />
      <PageContentWrapper style={{ padding: `80px 0 0 0` }}>
        <AppBar
          position="static"
          style={{ background: `${theme.palette.background.default}` }}
          elevation={0}
        >
          <AntTabs
            value={currentTab}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            {tabs.map((tab, index) => (
              <AntTab key={tab.name} label={tab.name} {...a11yProps(index)} />
            ))}
          </AntTabs>
        </AppBar>
        <Breadcrumbs
          style={{
            padding: `${theme.spacing(5)}px 0 ${theme.spacing(3)}px ${theme.spacing(6)}px`,
          }}
        >
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Link
            variant="body1"
            color="inherit"
            to={previousPath}
            component={RouterLink}
          >
            Pridruživanje odgovorne osobe
          </Link>
          <Typography variant="body1" color="textPrimary">
            {tabs[currentTab].extendedName ? tabs[currentTab].extendedName : tabs[currentTab].name}
          </Typography>
        </Breadcrumbs>
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab.name}
            value={currentTab}
            index={index}
            dir={theme.direction}
            theme={theme}
          >
            {tab.component}
          </TabPanel>
        ))}
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default Details;
